import { ref, reactive, computed, watch, onBeforeUnmount } from '@vue/composition-api'
import store from '@/store'
import { useLambda } from '@/views/habit/awsLambda'
import { oid } from '@/helpers/mongoHelpers'
import { computeResidual } from '@/lib/privacy/calculations'
import i18n from '@/libs/i18n'
import useNotifications from '@/composables/useNotifications'
import useCommon from '@/views/organization/useCommon'

export default function useARRForm(props, { root, emit }) {
  const { getItemsAggregate } = useLambda()
  const { showErrorMessage } = useNotifications()
  const { getWorkersSupervisedForDropDown, workersSupervised } = useCommon()
  const userData = store.state?.userStore?.userData
  const client_id = userData?.client?.$oid

  // Refs
  const arrFormRef = ref(null)
  const availableTreatmentsWithARI = ref([])
  const selectedTreatment = ref(null)
  const selectedInherentRisk = ref(null)
  const noARIWarning = ref(false)
  const existingARRWarning = ref(false)
  const existingARRId = ref(null)
  const isAutosaving = ref(false)
  const lastAutosaveTime = ref(null)
  let autosaveTimeout = null

  // Refs for validation observers
  const step1Tab = ref(null)
  const step2Tab = ref(null)
  const step3Tab = ref(null)
  const step4Tab = ref(null)
  const step5Tab = ref(null)

  // Tabs array for validation
  const tabs = [
    { name: 'step1', ref: step1Tab },
    { name: 'step2', ref: step2Tab },
    { name: 'step3', ref: step3Tab },
    { name: 'step4', ref: step4Tab },
    { name: 'step5', ref: step5Tab },
  ]

  // Form data
  const formData = reactive({
    treatment_id: null,
    inherentAnalysis_id: null,
    preventiveControls: '',
    preventiveEffectiveness: 1,
    correctiveControls: '',
    correctiveEffectiveness: 1,
    decision: null,
    actionPlan: '',
    responsible_id: null,
    targetDate: null,
    status: 'Pendiente',
  })

  const calculatedResults = reactive({
    // Inherent (from ARI)
    P_inh: null,
    I_inh: null,
    risk_inh: null,
    level_inh: '',
    // Residual (calculated)
    P_res: null,
    I_res: null,
    risk_res: null,
    level_res: '',
    reductionPct: null,
  })

  // Options
  const effectivenessOptions = ref([
    { value: 1, label: '1 - Nula', description: 'Sin efecto (multiplicador 1.00)' },
    { value: 2, label: '2 - Baja', description: 'Efecto limitado (multiplicador 0.80)' },
    { value: 3, label: '3 - Media', description: 'Efecto moderado (multiplicador 0.50)' },
    { value: 4, label: '4 - Alta', description: 'Efecto significativo (multiplicador 0.20)' },
    { value: 5, label: '5 - Muy Alta', description: 'Máxima eficacia (multiplicador 0.05)' },
  ])

  const decisionOptions = ref(['Aceptar', 'Mitigar', 'Transferir', 'Evitar'])
  const statusOptions = ref(['Pendiente', 'En progreso', 'Cerrado'])

  // Auto-calculate results when controls change
  watch(
    [() => formData.preventiveEffectiveness, () => formData.correctiveEffectiveness, selectedInherentRisk],
    () => {
      if (selectedInherentRisk.value) {
        recalculateResults()

        // Debounce autosave
        if (props.onSaveDraft) {
          if (autosaveTimeout) {
            clearTimeout(autosaveTimeout)
          }

          autosaveTimeout = setTimeout(() => {
            // Don't autosave if warnings are active
            if (noARIWarning.value || existingARRWarning.value) {
              return
            }

            const draftPayload = {
              treatment_id: formData.treatment_id,
              inherentAnalysis_id: formData.inherentAnalysis_id,
              inheritedP: calculatedResults.P_inh,
              inheritedI: calculatedResults.I_inh,
              inheritedRisk: calculatedResults.risk_inh,
              inheritedLevel: calculatedResults.level_inh || '',
              preventiveControls: formData.preventiveControls,
              preventiveEffectiveness: formData.preventiveEffectiveness,
              correctiveControls: formData.correctiveControls,
              correctiveEffectiveness: formData.correctiveEffectiveness,
              residualP: calculatedResults.P_res,
              residualI: calculatedResults.I_res,
              residualRisk: calculatedResults.risk_res,
              residualLevel: calculatedResults.level_res || '',
              riskReductionPct: calculatedResults.reductionPct,
              decision: formData.decision,
              actionPlan: formData.actionPlan,
              responsible_id: formData.responsible_id,
              targetDate: formData.targetDate,
              status: formData.status,
            }

            isAutosaving.value = true
            props.onSaveDraft(draftPayload)
              .catch(error => {
                console.error('Error in autosave:', error)
              })
              .finally(() => {
                isAutosaving.value = false
                lastAutosaveTime.value = new Date()
              })
          }, 1000)
        }
      }
    },
    { deep: true }
  )

  // Methods
  const fetchTreatmentsWithARI = async () => {
    try {
      // Fetch all treatments with completed ARI
      const pipeline = [
        {
          $match: {
            client_id: oid(client_id),
            deleted: { $ne: true },
            status: 'active',
          }
        },
        {
          $lookup: {
            from: 'inherentAnalysis',
            let: { treatment_id: '$_id' },
            pipeline: [
              {
                $match: {
                  $expr: {
                    $and: [
                      { $eq: ['$treatment_id', '$$treatment_id'] },
                      { $ne: ['$deleted', true] },
                      { $eq: ['$status', 'active'] }
                    ]
                  }
                }
              }
            ],
            as: 'inherentAnalysis'
          }
        },
        {
          $match: {
            'inherentAnalysis.0': { $exists: true }  // Only treatments with ARI
          }
        },
        {
          $project: {
            _id: 1,
            treatmentId: 1,
            nombre: 1,
            inherentAnalysis: { $arrayElemAt: ['$inherentAnalysis', 0] }
          }
        },
        { $sort: { treatmentId: 1 } }
      ]

      const result = await getItemsAggregate({ collection: 'treatment', pipeline })

      // Add display label
      availableTreatmentsWithARI.value = (result || []).map(item => ({
        ...item,
        displayLabel: `${item.treatmentId} - ${item.nombre}`
      }))
    } catch (error) {
      console.error('Error fetching treatments with ARI:', error)
      root.$toast.error('Error al cargar tratamientos')
    }
  }


  const handleTreatmentSelect = async treatmentId => {
    if (!treatmentId) {
      selectedTreatment.value = null
      selectedInherentRisk.value = null
      noARIWarning.value = false
      existingARRWarning.value = false
      existingARRId.value = null
      return
    }

    const item = availableTreatmentsWithARI.value.find(t => t._id === treatmentId)

    if (!item) {
      console.warn('Treatment not found in availableTreatmentsWithARI')
      return
    }

    selectedTreatment.value = {
      _id: item._id,
      treatmentId: item.treatmentId,
      nombre: item.nombre,
    }

    // Check if ARI exists
    if (!item.inherentAnalysis || !item.inherentAnalysis._id) {
      noARIWarning.value = true
      selectedInherentRisk.value = null
      formData.inherentAnalysis_id = null
      return
    }

    noARIWarning.value = false

    // Set inherent risk data (auto-pull from ARI)
    selectedInherentRisk.value = {
      _id: item.inherentAnalysis._id,
      calculatedP: item.inherentAnalysis.calculatedP,
      calculatedI: item.inherentAnalysis.calculatedI,
      inherentRisk: item.inherentAnalysis.inherentRisk,
      inherentLevel: item.inherentAnalysis.inherentLevel,
    }

    formData.inherentAnalysis_id = item.inherentAnalysis._id

    // Check for existing ARR for this treatment
    try {
      const query = {
        client_id: oid(client_id),
        treatment_id: oid(treatmentId),
        deleted: { $ne: true },
      }

      const pipeline = [
        { $match: query },
        { $project: { _id: 1 } },
        { $limit: 1 }
      ]

      const result = await getItemsAggregate({ collection: 'residualAnalysis', pipeline })

      if (result && result.length > 0) {
        const foundArrId = result[0]._id

        // In edit mode, only warn if the found ARR is different from the current one being edited
        if (props.currentArrId && foundArrId === props.currentArrId) {
          existingARRWarning.value = false
          existingARRId.value = null
        } else {
          existingARRWarning.value = true
          existingARRId.value = foundArrId
        }
      } else {
        existingARRWarning.value = false
        existingARRId.value = null
      }
    } catch (error) {
      console.error('Error checking existing ARR:', error)
    }

    // Recalculate with initial effectiveness values
    recalculateResults()
  }

  const recalculateResults = () => {
    if (!selectedInherentRisk.value) return

    // Set inherent values
    calculatedResults.P_inh = selectedInherentRisk.value.calculatedP
    calculatedResults.I_inh = selectedInherentRisk.value.calculatedI
    calculatedResults.risk_inh = selectedInherentRisk.value.inherentRisk
    calculatedResults.level_inh = selectedInherentRisk.value.inherentLevel || ''

    // Calculate residual
    const residual = computeResidual(
      calculatedResults.P_inh,
      calculatedResults.I_inh,
      formData.preventiveEffectiveness,
      formData.correctiveEffectiveness
    )

    calculatedResults.P_res = residual.P_res
    calculatedResults.I_res = residual.I_res
    calculatedResults.risk_res = residual.risk
    calculatedResults.level_res = residual.level || ''

    // Calculate reduction percentage
    if (calculatedResults.risk_inh && calculatedResults.risk_res != null) {
      const reduction = ((calculatedResults.risk_inh - calculatedResults.risk_res) / calculatedResults.risk_inh) * 100
      calculatedResults.reductionPct = Math.round(reduction)
    } else {
      calculatedResults.reductionPct = 0
    }
  }

  // Validation function for a specific tab
  const validateTab = (tabRef) => new Promise((resolve, reject) => {
    tabRef.value.validate().then(success => {
      if (success) {
        resolve(true)
      } else {
        showErrorMessage(i18n.t('message.requiredFieldsIncomplete'))
        reject(new Error('Validation failed'))
      }
    })
  })

  // Validate all tabs before submit
  const validateAllTabs = async () => {
    const wizard = arrFormRef.value

    for (let i = 0; i < wizard.tabs.length; i++) {
      try {
        await validateTab(tabs[i].ref)
      } catch (error) {
        // If validation fails, set active tab to the failed one
        if (i !== wizard.activeTabIndex) {
          wizard.changeTab(wizard.activeTabIndex, i)
        }
        throw error
      }
    }
  }

  const handleSubmit = async () => {
    try {
      // Block submission if warnings are active
      if (noARIWarning.value) {
        root.$toast.warning('Este tratamiento no tiene un ARI completo. Complete el ARI primero.')
        return
      }

      if (existingARRWarning.value) {
        root.$toast.warning('Este tratamiento ya tiene un ARR. Use el botón "Editar existente" para modificarlo.')
        return
      }

      await validateAllTabs()

      const payload = {
        treatment_id: formData.treatment_id,
        inherentAnalysis_id: formData.inherentAnalysis_id,
        inheritedP: calculatedResults.P_inh,
        inheritedI: calculatedResults.I_inh,
        inheritedRisk: calculatedResults.risk_inh,
        inheritedLevel: calculatedResults.level_inh || '',
        preventiveControls: formData.preventiveControls,
        preventiveEffectiveness: formData.preventiveEffectiveness,
        correctiveControls: formData.correctiveControls,
        correctiveEffectiveness: formData.correctiveEffectiveness,
        residualP: calculatedResults.P_res,
        residualI: calculatedResults.I_res,
        residualRisk: calculatedResults.risk_res,
        residualLevel: calculatedResults.level_res || '',
        riskReductionPct: calculatedResults.reductionPct,
        decision: formData.decision,
        actionPlan: formData.actionPlan,
        responsible_id: formData.responsible_id,
        targetDate: formData.targetDate,
        status: formData.status,
      }

      // Emit submit event with payload (handled by parent Add/Edit composable)
      emit('submit', payload)
    } catch (error) {
      // Validation failed, don't submit
    }
  }

  // Navigate to next tab with validation
  const goToNextTab = async () => {
    const index = arrFormRef.value.activeTabIndex

    // Block navigation if warnings are active
    if (noARIWarning.value || existingARRWarning.value) {
      if (noARIWarning.value) {
        root.$toast.warning('Este tratamiento no tiene un ARI completo.')
      } else {
        root.$toast.warning('Este tratamiento ya tiene un ARR. Use el botón "Editar existente".')
      }
      return
    }

    try {
      await validateTab(tabs[index].ref)
      arrFormRef.value.nextTab()
    } catch (err) {
      // Validation error, don't advance
    }
  }

  const goToPreviousTab = () => {
    arrFormRef.value?.prevTab()
  }

  const formatAutosaveTime = timestamp => {
    if (!timestamp) return ''
    const date = new Date(timestamp)
    return date.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })
  }

  // Cleanup timeout on component unmount
  onBeforeUnmount(() => {
    if (autosaveTimeout) {
      clearTimeout(autosaveTimeout)
    }
  })

  return {
    arrFormRef,
    availableTreatmentsWithARI,
    availableWorkers: workersSupervised,
    selectedTreatment,
    selectedInherentRisk,
    noARIWarning,
    existingARRWarning,
    existingARRId,
    isAutosaving,
    lastAutosaveTime,
    formData,
    calculatedResults,
    effectivenessOptions,
    decisionOptions,
    statusOptions,
    step1Tab,
    step2Tab,
    step3Tab,
    step4Tab,
    step5Tab,
    fetchTreatmentsWithARI,
    fetchWorkers: getWorkersSupervisedForDropDown,
    handleTreatmentSelect,
    recalculateResults,
    handleSubmit,
    goToNextTab,
    goToPreviousTab,
    formatAutosaveTime,
  }
}
